import { useQuery } from '@tanstack/react-query';
import {
  fetchEmailPreview,
  type EmailPreviewKind,
  type EmailPreviewVars,
} from '@/api/email-preview';

interface EmailPreviewProps {
  kind: EmailPreviewKind;
  resend?: boolean;
  // Entered display values (name/role/department/jobRole/recipientEmail/sentAt) so a pre-send
  // preview greets the real recipient. Omit for a standalone preview shortcut → the server falls
  // back to its sample values.
  vars?: EmailPreviewVars;
}

/**
 * Renders the SERVER-rendered email (subject + HTML body) from `GET /admin/email-previews/{kind}`.
 * The body HTML is the BE's single owner render (`messages_ko`) — the FE no longer builds its own template
 * (clients display the server render, never re-author it). The HTML is produced and
 * escaped server-side, so it is rendered as-is. Used by every email-preview page (admin + staff).
 */
export function EmailPreview({ kind, resend = false, vars }: EmailPreviewProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['email-preview', kind, resend, vars ?? null],
    queryFn: () => fetchEmailPreview(kind, resend, vars),
  });

  // Korean-only UI; trivial status strings (not template content — the body comes from the server).
  if (isLoading) {
    return <div className="email-preview-render email-preview-render--loading">미리보기를 불러오는 중…</div>;
  }
  if (isError || !data) {
    return <div className="email-preview-render email-preview-render--error">미리보기를 불러올 수 없습니다.</div>;
  }

  // Card frame + subject-as-title, matching the prototype (prototype/pages/staff-invite-email.html).
  // The card is preview presentation only; the email body inside (`data.html`) is still the BE
  // single-owner render, untouched.
  //
  // SHCT-651 — there is no FE-side Sender/Date/To box any more. `_layout.html :: page` already
  // renders 발신자 / 발송일시 / 받는사람 at the top of EVERY email, so painting our own copy above the
  // server render put the same header on screen twice. The subject <h1> below is NOT a duplicate:
  // the subject travels beside the body, never inside it.
  return (
    <article
      className="email-preview-render"
      style={{
        width: '100%',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
      }}
    >
      <h1
        className="email-preview-render__subject"
        style={{
          margin: 0,
          padding: '22px 24px',
          borderBottom: '1px solid #eceef2',
          fontSize: 18,
          fontWeight: 700,
          color: '#111827',
        }}
      >
        {data.subject}
      </h1>
      {/* SHCT-650 — the rendered body is INERT. It is real email markup, so it carries real
          anchors: the invite CTA is `<a th:href="${signupLink}">`, which in preview mode points at
          the sample host `https://admin.smarthomecare.example/...`. Injected via
          dangerouslySetInnerHTML those anchors are live, so clicking one navigated the admin app
          off to a domain that does not exist. A preview must show the mail, never act as it.

          Belt and braces on purpose: `pointer-events: none` stops the mouse, and the capture-phase
          onClick catches anything that still reaches a link (keyboard activation, a browser that
          ignores the style). `inert` removes the subtree from focus/AT navigation so Tab cannot
          land on a link either — it is ignored by browsers that do not support it, which is why it
          is not the only guard. */}
      <div
        className="email-preview-render__body"
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error `inert` is not in this React version's JSX typings yet; the DOM honours it.
        inert=""
        onClickCapture={(e) => e.preventDefault()}
        style={{ pointerEvents: 'none' }}
        dangerouslySetInnerHTML={{ __html: data.html }}
      />
    </article>
  );
}
